import { toast } from "sonner";
import {
	type Milestone,
	releaseMilestonePayment,
	updateMilestoneStatus,
} from "../../services/projects-api.service";

/**
 * Whether the homeowner's confirmation can still move money for this milestone.
 *
 * `fundingStatus === "funded"` is written by `finalizeProjectFunding` in the
 * same step that moves the escrow row to `held`, which is exactly what
 * `releaseMilestonePayment` requires. `released` is excluded: the payout has
 * already happened, and the button used to reappear on such a milestone because
 * the release leaves `status` at `pending`. `disputed` is excluded: its payment
 * moves only when the dispute is resolved.
 */
export function canConfirmCompletion(
	milestone: Pick<Milestone, "fundingStatus">,
): boolean {
	return milestone.fundingStatus === "funded";
}

/**
 * The homeowner confirms a milestone's work is done, which also pays the
 * designer: mark it completed if it is not, then release its payment. Shared,
 * with `canConfirmCompletion` as its gate, by the Milestones tab's "Confirm
 * completion" and the BOQ tab's payment list. The backend's release refuses a milestone
 * that is not completed, so a bare release from an in-progress milestone could
 * only fail.
 *
 * The two calls are not a transaction. If the release fails, the milestone is
 * left completed and still funded, so confirming again retries only the
 * release. Reports the outcome in a toast; resolves true when the payment was
 * released.
 */
export async function confirmCompletionAndRelease(
	milestone: Pick<Milestone, "id" | "status">,
): Promise<boolean> {
	try {
		if (milestone.status !== "completed") {
			const marked = await updateMilestoneStatus(milestone.id, "completed");
			if (!marked.success) {
				toast.error(marked.error || "Failed to confirm the milestone");
				return false;
			}
		}
		const result = await releaseMilestonePayment(milestone.id);
		if (result.success) {
			toast.success("Payment released to the designer");
			return true;
		}
		toast.error(result.error || "Failed to release payment");
		return false;
	} catch {
		// Either call can be the one that threw, so this says nothing about
		// which step reached the server.
		toast.error("Failed to confirm the milestone");
		return false;
	}
}
